import { useMsal } from '@azure/msal-react';
import { Loading } from '../../components/common/Loading';
import { ErrorState } from '../../components/common/ErrorState';
import React, { useEffect, useState } from 'react';
import { constructGetRequest, constructReportsUrl } from '../../controller/dashboardController';
import Configuration from '../../config/einvoicing-configuration';
import { AuthenticationResult } from '@azure/msal-browser';
import './reports.css'
import { LeakageReport } from './LeakageReport';
import { SimpleGroupedBarChart } from '../../components/charts/SimpleGroupedBarChart';
import { CountryCodeToFriendlyNameMapping } from '../../utils/FriendlyNameMappings';
import {
    Button,
    MessageBar,
    MessageBarTitle,
    MessageBarBody,
    MessageBarActions,
    TabList,
    Tab,
    SelectTabEvent,
    SelectTabData,
    Dropdown,
    Option,
    Label
} from "@fluentui/react-components";
import { DismissRegular, DocumentRegular, DataTrendingRegular } from "@fluentui/react-icons";
import { usePageTitle } from '../../hooks/usePageTitle';
interface ReportProps { }

interface IReportModeOption {
    key: string;
    text: string;
}


function Reports(props: ReportProps) {
usePageTitle('Leakage Reports');
const { instance, accounts } = useMsal();
    const [accessToken, setAccessToken] = useState('');
    const [mode, setMode] = useState('default');
    const [isLoading, setIsLoading]: [boolean, React.Dispatch<React.SetStateAction<boolean>>] = useState(true);
    const [isError, setIsError]: [boolean, React.Dispatch<React.SetStateAction<boolean>>] = useState(false);
    const [reports, setReports]: [any[], React.Dispatch<React.SetStateAction<any>>] = useState<any[]>([]);
    const [hideMessage, setHideMessage]: [{ [key: string]: boolean }, React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>] = useState<{ [key: string]: boolean }>({
        'FailedEventsMapper': true,
        "SLAMapper": true,
        "SLAApproachingMapper": true
    });
    const [message, setMessage]: [{ [key: string]: string }, React.Dispatch<React.SetStateAction<{ [key: string]: string }>>] = useState<{ [key: string]: string }>({
        'FailedEventsMapper': '',
        "SLAMapper": '',
        "SLAApproachingMapper": ''
    });
    const [analysisData, setAnalysisData]: [{ [key: string]: any }, React.Dispatch<React.SetStateAction<{ [key: string]: any }>>] = useState<{ [key: string]: any }>({
        'FailedEventsMapper': null
    });
    const [activeTab, setActiveTab]: [{ [key: string]: string }, React.Dispatch<React.SetStateAction<{ [key: string]: string }>>] = useState<{ [key: string]: string }>({
        'FailedEventsMapper': 'analysis'
    });

    async function getAccessToken() {
        if (accessToken === '') {
            const request = {
                ...Configuration.msalTokenRequest,
                account: accounts[0]
            };
            const result: AuthenticationResult = await instance.acquireTokenSilent(request);
            setAccessToken(result.idToken);
            return result.idToken
        }
        return accessToken;
    }

    const colors: { [key: string]: string } = {
        "HU": '#0078d4', // Blue
        "IN": '#d13438', // Red
        "IT": '#107c10', // Green
        "RS": '#ffaa44', // Orange
        "FI": '#881798', // Purple
        "NO": '#00b7c3', // Teal
        "DK": '#e3008c', // Hot Pink
        "SE": '#69797e', // Gray
        "DE": '#00cc6a', // Green
        "SA": '#038387', // Cyan
        "US": '#4b65e1', // Indigo
        "BR": '#8764b8', // Violet
    }
    
    const handleLegendClick = (reportType: string, legend: string, key: string) => {
        const reportIndex = reportType === 'FailedEventsMapper' ? 0 : reportType === 'SLAMapper' ? 1 : 2;
        const report = reports[reportIndex];
        if (!report) return;

        const allDocumentIds: string[] = [];
        let combinedAnalysisHTML = '';

        for (const dateGroup of report) {
            const seriesItem = dateGroup.series.find((s: any) => s.key === key);
            if (seriesItem && seriesItem.documentIds) {
                allDocumentIds.push(...seriesItem.documentIds);
                if (seriesItem.analysisHTML) {
                    combinedAnalysisHTML = seriesItem.analysisHTML;
                }
            }
        }

        if (allDocumentIds.length > 0) {
            setMessage({
                ...message, [reportType]: allDocumentIds.join(", ")
            });
            setAnalysisData({
                ...analysisData, [reportType]: { analysisHTML: combinedAnalysisHTML }
            });
            setHideMessage({
                ...hideMessage, [reportType]: false
            });
        }
    };

    function useReports(mode: string) {
        useEffect(() => {
            const reportsUrl: string = constructReportsUrl(Configuration.eventServiceUrl, mode);
            async function startLatency() {
                if (!ignore) {
                    try {
                        const requestParams = constructGetRequest(await getAccessToken());
                        const result = await fetch(reportsUrl, requestParams);

                        const reports: any[] = []
                        if (result !== undefined && result.ok) {
                            const resultJson: LeakageReport = await result.json()
                            if ('FailedEventsMapper' in resultJson && resultJson['FailedEventsMapper'] !== null && resultJson['FailedEventsMapper'] !== undefined) {

                                addReports(resultJson, reports, 'FailedEventsMapper');
                            }
                            if ('SLAMapper' in resultJson && resultJson['SLAMapper'] !== null && resultJson['SLAMapper'] !== undefined) {

                                addReports(resultJson, reports, 'SLAMapper');
                            }
                            if ('SLAApproachingMapper' in resultJson && resultJson['SLAApproachingMapper'] !== null && resultJson['SLAApproachingMapper'] !== undefined) {

                                addReports(resultJson, reports, 'SLAApproachingMapper');
                            }
                            setReports(reports)
                            setIsLoading(false);
                        }
                        else {
                            setIsLoading(false);
                            setIsError(true);
                        }
                    }
                    catch {
                        setIsLoading(false);
                        setIsError(true);
                    }
                }
            }

            let ignore = false;
            startLatency();
            return () => {
                ignore = true;
            }
        }, [mode]);
    }

    useReports(mode);

    const onDropdownChange = (event: any, data: any): void => {
        setMode(data.optionValue as string);
    };

    const onTabSelect = (event: SelectTabEvent, data: SelectTabData, reportType: string) => {
        setActiveTab({
            ...activeTab,
            [reportType]: data.value as string
        });
    };

    const modeOptions: IReportModeOption[] = [
        {
            key: 'default',
            text: 'Live'
        },
        {
            key: 'uat',
            text: 'UAT'
        },
        {
            key: 'test',
            text: 'Test'
        }
    ];

    if (isLoading) {
        return <Loading label='Loading Data...' />
    }
    else if (!isError) {

        return (
            <div>
                <h1>Leakage Reports</h1>
                <div className='reports-container'>
                    <div className='reports-dropdown-container'>
                        <Label>Mode</Label>
                        <Dropdown 
                            className="reports-dropdown" 
                            onOptionSelect={onDropdownChange} 
                            value={modeOptions.find(o => o.key === mode)?.text}
                            selectedOptions={[mode]}
                            aria-label="Report mode"
                        >
                            {modeOptions.map((option) => (
                                <Option key={option.key} value={option.key} text={option.text}>
                                    {option.text}
                                </Option>
                            ))}
                        </Dropdown>
                    </div>
                </div>
                {/* <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', marginTop: '2%' }}>
                    <h4>Failed Transactions in Past 1 month</h4>
                </div> */}
                <div style={{ width: '100%', display: 'flex', marginTop: '2%' }}>
                    <SimpleGroupedBarChart
                        chartTitle="Failed Transactions in Past 1 month"
                        data={reports[0]}
                        height={300}
                        isCalloutForStack={false}
                        hideLabels={false}
                        chartLabel="Failed Transactions in Past 1 month"
                        onLegendClick={(legend, key) => handleLegendClick('FailedEventsMapper', legend, key)}
                    />
                </div>
                
                {!hideMessage['FailedEventsMapper'] && 
                <div style={{ width: '100%', marginTop: '2%' }}>
                    <MessageBar shape="square" layout="multiline">
                        <MessageBarBody>
                            <div style={{ marginBottom: '10px' }}>
                                <TabList 
                                    selectedValue={activeTab['FailedEventsMapper']} 
                                    onTabSelect={(e, d) => onTabSelect(e, d, 'FailedEventsMapper')}
                                    size="small"
                                    appearance="subtle"
                                    aria-label="Failed transactions report view"
                                >
                                    {analysisData['FailedEventsMapper'] && analysisData['FailedEventsMapper'].analysisHTML && (
                                        <Tab value="analysis" icon={<DataTrendingRegular />}>Analysis</Tab>
                                    )}
                                    <Tab value="documents" icon={<DocumentRegular />}>Document IDs</Tab>
                                </TabList>
                            </div>
                            
                            {activeTab['FailedEventsMapper'] === 'analysis' && analysisData['FailedEventsMapper'] && analysisData['FailedEventsMapper'].analysisHTML ? (
                                <div dangerouslySetInnerHTML={{__html: analysisData['FailedEventsMapper'].analysisHTML}} />
                            ) : (
                                <div>
                                    <MessageBarTitle>Failed Document Ids - </MessageBarTitle>
                                    {message['FailedEventsMapper']}
                                </div>
                            )}
                        </MessageBarBody>
                        <MessageBarActions
                            containerAction={
                                <Button
                                    aria-label="dismiss"
                                    appearance="transparent"
                                    icon={<DismissRegular />}
                                    onClick={() => {
                                        hideMessage['FailedEventsMapper'] = true;
                                        setHideMessage({
                                            ...hideMessage, 'FailedEventsMapper': true
                                        });
                                    }}
                                />
                            }
                        >
                        </MessageBarActions>
                    </MessageBar>
                </div>}
                <div style={{ width: '100%', display: 'flex', marginTop: '2%' }}>
                    <SimpleGroupedBarChart
                        chartTitle="SLA Breached Transactions in Past 1 month"
                        data={reports[1]}
                        height={300}
                        isCalloutForStack={false}
                        hideLabels={false}
                        chartLabel="SLA Breached Transactions in Past 1 month"
                        onLegendClick={(legend, key) => handleLegendClick('SLAMapper', legend, key)}
                    />
                </div>

                {!hideMessage['SLAMapper'] && < div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', marginTop: '2%' }}>
                    <MessageBar shape="square" layout='multiline'>
                        <MessageBarBody>
                            <MessageBarTitle>SLA Breached Document Ids - </MessageBarTitle>
                            {message['SLAMapper']}
                        </MessageBarBody>
                        <MessageBarActions
                            containerAction={
                                <Button
                                    aria-label="dismiss"
                                    appearance="transparent"
                                    icon={<DismissRegular />}
                                    onClick={() => {
                                        hideMessage['SLAMapper'] = true;
                                        setHideMessage({
                                            ...hideMessage, 'SLAMapper': true
                                        });
                                    }}
                                />
                            }
                        >
                        </MessageBarActions>
                    </MessageBar>
                </div>}
                <div style={{ width: '100%', display: 'flex', marginTop: '2%' }}>
                    <SimpleGroupedBarChart
                        chartTitle="SLA Approaching Inprogress Transactions in Past 1 month"
                        data={reports[2]}
                        height={300}
                        isCalloutForStack={false}
                        hideLabels={false}
                        chartLabel="SLA Approaching Inprogress Transactions in Past 1 month"
                        onLegendClick={(legend, key) => handleLegendClick('SLAApproachingMapper', legend, key)}
                    />
                </div>

                {!hideMessage['SLAApproachingMapper'] && < div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', marginTop: '2%' }}>
                    <MessageBar shape="square" layout='multiline'>
                        <MessageBarBody>
                            <MessageBarTitle>SLA Approaching Document Ids - </MessageBarTitle>
                            {message['SLAApproachingMapper']}
                        </MessageBarBody>
                        <MessageBarActions
                            containerAction={
                                <Button
                                    aria-label="dismiss"
                                    appearance="transparent"
                                    icon={<DismissRegular />}
                                    onClick={() => {
                                        setHideMessage({
                                            ...hideMessage, 'SLAApproachingMapper' : true });
                                    }}
                                />
                            }
                        >
                        </MessageBarActions>
                    </MessageBar>
                </div>}
            </div>
        );
    }
    else {
        return (
            <ErrorState
                title="There was an issue loading the page"
                description="Check your internet connection, try refreshing the page or contact support."
                action={{
                    label: 'Refresh page',
                    onClick: () => window.location.reload()
                }}
            />);
    }

    function addReports(resultJson: LeakageReport, reports: any[], reportName: string) {
        const reportData: any = [];
        for (let date in resultJson[reportName]) {
            let series = [];
            for (let document of resultJson[reportName][date]) {
                if (document.value > 0) {
                    series.push(
                        {
                            key: document.countryCode,
                            data: document.value > 100 ? 100 : document.value,
                            legend: CountryCodeToFriendlyNameMapping[document.countryCode],
                            color: colors[document.countryCode],
                            xAxisCalloutData: date,
                            yAxisCalloutData: document.value,
                            onClick: function setInfoMessage() {
                                // Just store the analysis HTML directly from the document
                                const analysis = {
                                    analysisHTML: document.analysisHTML
                                };
                                setHideMessage({
                                    ...hideMessage, [document.documentType]: false
                                });
                                setMessage({
                                    ...message, [document.documentType]: document.documentIds.join(", ")
                                });
                                setAnalysisData({
                                    ...analysisData, [document.documentType]: analysis
                                });
                            },
                            documentType: reportName,
                            documentIds: document.documentIds,
                            analysisHTML: document.analysisHTML
                        }
                    );
                }
            }
            reportData.push({
                name: date,
                series: series
            });
        }
        reports.push(reportData);
    }
}

export default Reports;